import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Link, useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { exportProductSoldCsv, fetchProductSoldReport } from "../../../../services/reports";
import { UITableShell, UITableToolbar } from "../../../../components/ui";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import { parseSavedBoolean, normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportSecondaryShellSx,
  reportShellSx,
  reportSummaryCardSx,
  reportTableBodySx,
  reportTableContainerSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
  reportTableSectionSx,
} from "../shared/reportLayout";

const currency = (value) => `CA$${(Number(value) || 0).toFixed(2)}`;

const DEFAULT_SUMMARY = {
  totalItemsSold: 0,
  totalRevenue: 0,
  totalProfit: 0,
  productCount: 0,
  supplierCount: 0,
  totalRecords: 0,
};

const DEFAULT_FILTERS = {
  quickFind: "",
  dateFrom: "",
  dateTo: "",
  periodPreset: "fiscalYear",
  supplier: "",
  clientRep: "",
  client: "",
  industry: "",
  clientTags: "",
  productTags: "",
  includeInactive: false,
};

const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    return normalizeAvatarUrl(selectedPic?.url || null);
  }
  if (typeof imgSrc === "string") {
    if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
    if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
    if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      return imgSrc;
    }
  }
  return imgSrc;
};

const resolveProductImageUrl = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    const selected = value.find((entry) => entry?.selected || entry?.isPrimary) || value[0];
    return resolveProductImageUrl(
      selected?.url || selected?.ImageUrl || selected?.imageUrl || selected?.src || selected,
    );
  }
  if (typeof value === "object") {
    return resolveProductImageUrl(
      value.url || value.ImageUrl || value.imageUrl || value.src || value.href,
    );
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) || (parsed && typeof parsed === "object")) {
        return resolveProductImageUrl(parsed);
      }
    } catch {
      // fall through
    }
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${trimmed}`;
    if (trimmed.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${trimmed}`;
    return trimmed;
  }
  return null;
};

const productInitials = (name) =>
  String(name || "")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "P";

const ProductNameCell = ({ row }) => {
  const imageUrl = resolveProductImageUrl(row?.productImageUrl);
  const productName = row?.productName || "Unknown Product";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }} title={productName}>
      <Avatar
        src={imageUrl || undefined}
        alt={productName}
        sx={{
          width: 30,
          height: 30,
          fontSize: 12,
          bgcolor: "rgba(15, 23, 42, 0.08)",
          color: "text.secondary",
          flex: "0 0 auto",
          "& img": { objectFit: "cover" },
        }}
      >
        {productInitials(productName)}
      </Avatar>
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {productName}
      </Box>
    </Box>
  );
};

const ProjectLinkCell = ({ row, instanceUniqueId }) => {
  const projectNumber = row?.projectNumber || row?.orderNumber;
  const project = row?.project || projectNumber || "\u2014";
  if (!instanceUniqueId || !projectNumber) return <span>{project}</span>;

  return (
    <Link
      to={`/instance/${instanceUniqueId}/sales/orders/${projectNumber}/overview`}
      onClick={(event) => event.stopPropagation()}
      style={{ color: "#1976d2", textDecoration: "none", fontWeight: 600 }}
      title={`Open order ${projectNumber}`}
    >
      {project}
    </Link>
  );
};

const repDisplayName = (rep) =>
  [rep?.first_name || rep?.firstName, rep?.last_name || rep?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim()
  || rep?.email
  || (rep?.id != null ? `User ${rep.id}` : "");

const RepList = ({ reps, fallback = "Unassigned" }) => {
  const list = Array.isArray(reps) ? reps.filter(Boolean) : [];
  if (!list.length) return <span>{fallback || "Unassigned"}</span>;

  const allNames = list.map(repDisplayName).filter(Boolean).join(", ") || fallback || "Unassigned";
  const initials = (name) =>
    String(name || "")
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }} title={allNames}>
      {list.slice(0, 3).map((rep, index) => (
        <Avatar
          key={`${rep?.id || rep?.email || repDisplayName(rep) || "rep"}-${index}`}
          src={normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined}
          alt={repDisplayName(rep)}
          sx={{ width: 26, height: 26, fontSize: 12 }}
        >
          {initials(repDisplayName(rep))}
        </Avatar>
      ))}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {allNames}
      </Box>
      {list.length > 3 ? <Chip size="small" variant="outlined" label={`+${list.length - 3}`} /> : null}
    </Box>
  );
};

const getColumnValue = (row, field) => (!row || !field ? undefined : field.split(".").reduce((value, key) => value?.[key], row));

const compareValues = (a, b) => {
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  return String(a || "").localeCompare(String(b || ""));
};

const getColumnKey = (column = {}) => String(column.field || column.headerName || "").trim();

const ReportTable = ({
  rows = [],
  columns = [],
  emptyMessage = "No data available.",
  sortField = "totalRevenue",
  sortDirection = "desc",
  onSortChange = () => {},
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  const sortedRows = useMemo(() => {
    const nextRows = [...safeRows];
    if (!sortField) return nextRows;
    nextRows.sort((left, right) => {
      const result = compareValues(getColumnValue(left, sortField), getColumnValue(right, sortField));
      return sortDirection === "desc" ? -result : result;
    });
    return nextRows;
  }, [safeRows, sortDirection, sortField]);

  if (!safeRows.length) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography>{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...reportTableContainerSx,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "visible",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
        <TableHead>
          <TableRow>
            {safeColumns.map((column) => {
              const active = sortField === column.field;
              const nextDirection = active && sortDirection === "asc" ? "desc" : "asc";
              return (
                <TableCell
                  key={getColumnKey(column)}
                  onClick={() => onSortChange({ field: column.field, direction: nextDirection })}
                  sx={reportTableHeaderCellSx({
                    active,
                    sortable: true,
                    minWidth: column.minWidth || 120,
                  })}
                >
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <Box component="span">{column.headerName || column.field}</Box>
                    {active ? (
                      sortDirection === "asc"
                        ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    ) : null}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={reportTableBodySx}>
          {sortedRows.map((row, rowIndex) => (
            <TableRow key={row?.id || `${rowIndex}`} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
              {safeColumns.map((column) => {
                const value = getColumnValue(row, column.field);
                const renderedValue = typeof column.renderCell === "function"
                  ? column.renderCell({ value, row })
                  : (value ?? "\u2014");
                return (
                  <TableCell key={getColumnKey(column)} sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                    {renderedValue}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ProductSoldReport = () => {
  const { instanceUniqueId } = useParams();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [groupedSummary, setGroupedSummary] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [sortField, setSortField] = useState("totalRevenue");
  const [sortDirection, setSortDirection] = useState("desc");
  const [groupSortField, setGroupSortField] = useState("totalRevenue");
  const [groupSortDirection, setGroupSortDirection] = useState("desc");
  const reportRequestSeq = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("eliteCore:reports:productSold:columns");
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenColumns(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("eliteCore:reports:productSold:columns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const load = useCallback(async (overrideFilters = null) => {
    const requestId = ++reportRequestSeq.current;
    setLoading(true);
    try {
      const effectiveFilters = overrideFilters && typeof overrideFilters === "object"
        ? overrideFilters
        : filters;
      const res = await fetchProductSoldReport({ instanceUniqueId, ...effectiveFilters });
      if (requestId !== reportRequestSeq.current) return;
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setSummary(
        res?.summary && typeof res.summary === "object"
          ? { ...DEFAULT_SUMMARY, ...res.summary }
          : DEFAULT_SUMMARY,
      );
      setGroupedSummary(Array.isArray(res?.groupedSummary) ? res.groupedSummary : []);
    } catch (error) {
      if (requestId !== reportRequestSeq.current) return;
      console.error("Failed to load product sold report", error);
      setRows([]);
      setGroupedSummary([]);
      setSummary(DEFAULT_SUMMARY);
    } finally {
      if (requestId === reportRequestSeq.current) setLoading(false);
    }
  }, [filters, instanceUniqueId]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const res = await fetchProductSoldReport({ instanceUniqueId, ...filters });
        setRows(Array.isArray(res?.rows) ? res.rows : []);
        setSummary(
          res?.summary && typeof res.summary === "object"
            ? { ...DEFAULT_SUMMARY, ...res.summary }
            : DEFAULT_SUMMARY,
        );
        setGroupedSummary(Array.isArray(res?.groupedSummary) ? res.groupedSummary : []);
      } catch (error) {
        console.error("Failed to load product sold report", error);
        setRows([]);
        setGroupedSummary([]);
        setSummary(DEFAULT_SUMMARY);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
    // Intentionally reload only when the instance changes; other filter changes remain manual via Get Report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  const columns = useMemo(() => ([
    {
      field: "productName",
      headerName: "Product Name",
      minWidth: 260,
      renderCell: ({ row }) => <ProductNameCell row={row} />,
    },
    { field: "supplierName", headerName: "Supplier Name", minWidth: 180 },
    { field: "sku", headerName: "SKU", minWidth: 140 },
    { field: "itemsSold", headerName: "Items Sold" },
    { field: "totalRevenue", headerName: "Total Revenue", renderCell: (params) => currency(params.value) },
    { field: "profit", headerName: "Profit", renderCell: (params) => currency(params.value) },
    {
      field: "project",
      headerName: "Project",
      minWidth: 180,
      renderCell: ({ row }) => <ProjectLinkCell row={row} instanceUniqueId={instanceUniqueId} />,
    },
    { field: "customerName", headerName: "Client", minWidth: 180 },
    {
      field: "representatives",
      headerName: "Representatives",
      minWidth: 240,
      renderCell: ({ row }) => <RepList reps={row.repDetails} fallback={row.representatives} />,
    },
  ]), [instanceUniqueId]);

  const groupedColumns = useMemo(() => ([
    {
      field: "productName",
      headerName: "Product Name",
      minWidth: 260,
      renderCell: ({ row }) => <ProductNameCell row={row} />,
    },
    { field: "supplierName", headerName: "Supplier Name", minWidth: 180 },
    { field: "sku", headerName: "SKU" },
    { field: "itemsSold", headerName: "Items Sold" },
    { field: "totalRevenue", headerName: "Total Revenue", renderCell: (params) => currency(params.value) },
    { field: "profit", headerName: "Profit", renderCell: (params) => currency(params.value) },
    { field: "records", headerName: "Records" },
  ]), []);

  useEffect(() => {
    const validColumnKeys = new Set(
      [...columns, ...groupedColumns].map((column) => getColumnKey(column)).filter(Boolean),
    );
    setHiddenColumns((previous) => {
      const next = (Array.isArray(previous) ? previous : []).filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? [] : next;
    });
  }, [columns, groupedColumns]);

  const visibleColumns = useMemo(() => {
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    const nextColumns = columns.filter((column) => !hiddenSet.has(getColumnKey(column)));
    return nextColumns.length ? nextColumns : columns;
  }, [columns, hiddenColumns]);

  const visibleGroupedColumns = useMemo(() => {
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    const nextColumns = groupedColumns.filter((column) => !hiddenSet.has(getColumnKey(column)));
    return nextColumns.length ? nextColumns : groupedColumns;
  }, [groupedColumns, hiddenColumns]);

  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: {
      quickFind: filters.quickFind || "",
      dateFrom: filters.dateFrom || "",
      dateTo: filters.dateTo || "",
      periodPreset: filters.periodPreset || "fiscalYear",
      supplier: filters.supplier || "",
      clientRep: filters.clientRep || "",
      client: filters.client || "",
      industry: filters.industry || "",
      clientTags: filters.clientTags || "",
      productTags: filters.productTags || "",
      includeInactive: Boolean(filters.includeInactive),
    },
    hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
    sort: {
      field: sortField,
      direction: sortDirection,
    },
    groupedSort: {
      field: groupSortField,
      direction: groupSortDirection,
    },
  }), [filters, groupSortDirection, groupSortField, hiddenColumns, sortDirection, sortField]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object"
      ? savedState.filters
      : {};
    const validColumnKeys = new Set(
      [...columns, ...groupedColumns].map((column) => getColumnKey(column)).filter(Boolean),
    );

    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: Array.from(validColumnKeys),
      fallbackHiddenColumns: [],
    });

    const nextSortField = columns.some((column) => column.field === savedState?.sort?.field)
      ? savedState.sort.field
      : "totalRevenue";
    const nextSortDirection = savedState?.sort?.direction === "asc" ? "asc" : "desc";
    const nextGroupSortField = groupedColumns.some((column) => column.field === savedState?.groupedSort?.field)
      ? savedState.groupedSort.field
      : "totalRevenue";
    const nextGroupSortDirection = savedState?.groupedSort?.direction === "asc" ? "asc" : "desc";

    const nextFilters = {
      ...DEFAULT_FILTERS,
      ...savedFilters,
      quickFind: String(savedFilters?.quickFind || ""),
      dateFrom: String(savedFilters?.dateFrom || ""),
      dateTo: String(savedFilters?.dateTo || ""),
      periodPreset: String(savedFilters?.periodPreset || "fiscalYear"),
      supplier: String(savedFilters?.supplier || ""),
      clientRep: String(savedFilters?.clientRep || ""),
      client: String(savedFilters?.client || ""),
      industry: String(savedFilters?.industry || ""),
      clientTags: String(savedFilters?.clientTags || ""),
      productTags: String(savedFilters?.productTags || ""),
      includeInactive: parseSavedBoolean(savedFilters?.includeInactive, false),
    };

    setHiddenColumns(nextHiddenColumns);
    setSortField(nextSortField);
    setSortDirection(nextSortDirection);
    setGroupSortField(nextGroupSortField);
    setGroupSortDirection(nextGroupSortDirection);
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, groupedColumns, load]);

  const summaryCards = [
    ["Items Sold", summary.totalItemsSold ?? 0],
    ["Total Revenue", currency(summary.totalRevenue)],
    ["Profit", currency(summary.totalProfit)],
    ["Products", summary.productCount ?? 0],
    ["Suppliers", summary.supplierCount ?? 0],
    ["Total Records", summary.totalRecords ?? 0],
  ];

  const onExport = async () => {
    const response = await exportProductSoldCsv({ instanceUniqueId, ...filters });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "text/csv;charset=utf-8;" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "product-sold-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                sx={{ width: "100%" }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                    Product Sold Report
                  </Typography>
                  <Chip
                    label={loading ? "Loading..." : `${Array.isArray(rows) ? rows.length : 0} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label="productSold"
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-info-bg)",
                      color: "var(--ec-status-info-text)",
                      border: "1px solid var(--ec-status-info-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                      textTransform: "none",
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="productSold"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Row density">
                    <span>
                      <IconButton size="small" disabled sx={{ color: "text.secondary" }}>
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={onExport}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>

            <Box sx={{ ...reportContentSx, minWidth: 0, maxWidth: "100%" }}>
              <Paper elevation={0} sx={reportFilterCardSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Product Sold Report Controls
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Analyze product volume, revenue, profit, suppliers, clients, and representatives.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" size="small" onClick={load}>
                      Get Report
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setFilters(DEFAULT_FILTERS)}>
                      Reset
                    </Button>
                  </Stack>
                </Box>

                <Box sx={reportFilterGridSx}>
                  <TextField
                    label="Search"
                    value={filters.quickFind}
                    onChange={(event) => setFilters((previous) => ({ ...previous, quickFind: event.target.value }))}
                    size="small"
                  />
                  <TextField
                    label="Date From"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      dateFrom: event.target.value,
                      periodPreset: "custom",
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Date To"
                    type="date"
                    value={filters.dateTo}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      dateTo: event.target.value,
                      periodPreset: "custom",
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    select
                    label="Period Preset"
                    value={filters.periodPreset}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      periodPreset: event.target.value,
                    }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="fiscalYear">Fiscal Year</option>
                    <option value="calendarYear">Calendar Year</option>
                    <option value="custom">Custom Range</option>
                  </TextField>
                  <TextField
                    label="Supplier"
                    value={filters.supplier}
                    onChange={(event) => setFilters((previous) => ({ ...previous, supplier: event.target.value }))}
                    size="small"
                  />
                  <TextField
                    label="Client Rep"
                    value={filters.clientRep}
                    onChange={(event) => setFilters((previous) => ({ ...previous, clientRep: event.target.value }))}
                    size="small"
                    helperText="ID or name"
                  />
                  <TextField
                    label="Client"
                    value={filters.client}
                    onChange={(event) => setFilters((previous) => ({ ...previous, client: event.target.value }))}
                    size="small"
                  />
                  <TextField
                    label="Industry"
                    value={filters.industry}
                    onChange={(event) => setFilters((previous) => ({ ...previous, industry: event.target.value }))}
                    size="small"
                  />
                  <TextField
                    label="Client Tags"
                    value={filters.clientTags}
                    onChange={(event) => setFilters((previous) => ({ ...previous, clientTags: event.target.value }))}
                    size="small"
                  />
                  <TextField
                    label="Product Tags"
                    value={filters.productTags}
                    onChange={(event) => setFilters((previous) => ({ ...previous, productTags: event.target.value }))}
                    size="small"
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={filters.includeInactive}
                        onChange={(event) => setFilters((previous) => ({
                          ...previous,
                          includeInactive: event.target.checked,
                        }))}
                      />
                    )}
                    label="Include Inactive"
                  />
                </Box>
              </Paper>

              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(6, minmax(0, 1fr))",
                  },
                }}
              >
                {summaryCards.map(([label, value]) => (
                  <Paper key={label} elevation={0} sx={reportSummaryCardSx}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Box sx={{ ...reportTableSectionSx, minWidth: 0, maxWidth: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{ ...reportTablePaperSx, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
                >
                  <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      {loading ? (
                        <Box sx={{ p: 4 }}>
                          <Typography>Loading product sold report...</Typography>
                        </Box>
                      ) : (
                        <ReportTable
                          rows={rows}
                          columns={visibleColumns}
                          emptyMessage="No product sales found for selected filters."
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSortChange={({ field, direction }) => {
                            setSortField(field);
                            setSortDirection(direction);
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {!loading && groupedSummary.length > 0 ? (
                <UITableShell paperSx={reportSecondaryShellSx}>
                  <UITableToolbar sx={{ backgroundColor: "background.paper" }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Product Summary
                    </Typography>
                  </UITableToolbar>
                  <Box sx={{ p: { xs: 1, md: 1.5 }, minWidth: 0, maxWidth: "100%" }}>
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          overflowX: "auto",
                          overflowY: "hidden",
                        }}
                      >
                        <ReportTable
                          rows={groupedSummary}
                          columns={visibleGroupedColumns}
                          emptyMessage="No product summary data."
                          sortField={groupSortField}
                          sortDirection={groupSortDirection}
                          onSortChange={({ field, direction }) => {
                            setGroupSortField(field);
                            setGroupSortDirection(direction);
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </UITableShell>
              ) : null}
            </Box>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
};

export default ProductSoldReport;
